import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

import type { DevelopmentDetailDocumentResponse } from '@/domain/models/development.js';
import { FileUpload } from '@/presentation/components/index.js';
import { DocumentDownloadRow } from '@/presentation/pages/developments/document-download-row.js';
import { FinalizationCard } from '@/presentation/pages/developments/documents/finalization-card.js';
import type { DevelopmentStatus } from '@/shared/utils/format-status.js';

const BASE_DOC_TYPES = ['DOCUMENTACAO_TECNICA', 'EVIDENCIA_TESTES_DEV'] as const;

const CUTOVER_DOC_TYPE = 'DOCUMENTO_CUTOVER';

type RequiredDocType = (typeof BASE_DOC_TYPES)[number] | typeof CUTOVER_DOC_TYPE;

const DOC_LABEL_KEYS: Record<RequiredDocType, string> = {
    DOCUMENTACAO_TECNICA: 'documents.technicalDoc',
    EVIDENCIA_TESTES_DEV: 'documents.testEvidence',
    DOCUMENTO_CUTOVER: 'documents.cutoverDoc'
};

const DOC_DESC_KEYS: Record<RequiredDocType, string> = {
    DOCUMENTACAO_TECNICA: 'documents.technicalDocDesc',
    EVIDENCIA_TESTES_DEV: 'documents.testEvidenceDesc',
    DOCUMENTO_CUTOVER: 'documents.cutoverDocDesc'
};

export interface DocumentsTabProps {
    status: DevelopmentStatus;
    documents: DevelopmentDetailDocumentResponse[];
    canFinalizeDev: boolean;
    uploading: boolean;
    startDate: string | null;
    finishing: boolean;
    cutoverNotRequired: boolean;
    cutoverToggling: boolean;
    onToggleCutover: (value: boolean) => void;
    onUpload: (docType: string, file: File) => void;
    onRegisterDevEnd: (endDate: string) => void;
    onDownloadDocument: (docId: string) => void;
    translate: (key: string, options?: Record<string, unknown>) => string;
}

export function DocumentsTab({ status, documents, canFinalizeDev, uploading, startDate, finishing, cutoverNotRequired, cutoverToggling, onToggleCutover, onUpload, onRegisterDevEnd, onDownloadDocument, translate }: DocumentsTabProps) {
    const inDevelopment = status === 'EM_DESENVOLVIMENTO' || status === 'IMPEDIDO';
    const canUpload = canFinalizeDev && inDevelopment;
    const submitted = !inDevelopment && status !== 'AGUARDANDO_INICIO';
    const hasCutoverDoc = documents.some((d) => d.docType === CUTOVER_DOC_TYPE && d.isCurrent);
    const requiredDocTypes: readonly RequiredDocType[] = cutoverNotRequired ? BASE_DOC_TYPES : [...BASE_DOC_TYPES, CUTOVER_DOC_TYPE];
    const showCutoverCard = !cutoverNotRequired;
    const allUploaded = requiredDocTypes.every((docType) => documents.some((d) => d.docType === docType && d.isCurrent));

    let bottomSection: ReactNode = null;
    if (submitted) {
        bottomSection = (
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="body2" color="text.secondary">
                        {translate('documents.docsReadyForReview')}
                    </Typography>
                </CardContent>
            </Card>
        );
    } else if (inDevelopment && canFinalizeDev) {
        bottomSection = <FinalizationCard startDate={startDate} allUploaded={allUploaded} submitting={finishing} totalDocs={requiredDocTypes.length} onSubmit={onRegisterDevEnd} translate={translate} />;
    }

    const renderDocCard = (docType: RequiredDocType) => {
        const doc = documents.find((d) => d.docType === docType && d.isCurrent);
        return (
            <Card key={docType} variant="outlined">
                <CardContent>
                    <Typography variant="subtitle2" fontWeight={600}>
                        {translate(DOC_LABEL_KEYS[docType])}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {translate(DOC_DESC_KEYS[docType])}
                    </Typography>
                    {doc && (
                        <Stack sx={{ mb: canUpload ? 1 : 0 }}>
                            <DocumentDownloadRow
                                label={doc.fileName}
                                uploadedAt={doc.uploadedAt}
                                downloadAriaLabel={`${translate('documents.download')}: ${doc.fileName}`}
                                onDownload={() => onDownloadDocument(doc.id)}
                            />
                        </Stack>
                    )}
                    {!doc && !canUpload && (
                        <Typography variant="body2" color="text.disabled">
                            {translate('documents.noDocUploaded')}
                        </Typography>
                    )}
                    {canUpload && <FileUpload onFileSelected={(f) => onUpload(docType, f)} maxSize={20 * 1024 * 1024} uploading={uploading} />}
                </CardContent>
            </Card>
        );
    };

    const cutoverDisabled = !canFinalizeDev || !inDevelopment || cutoverToggling;

    return (
        <Stack spacing={2}>
            {BASE_DOC_TYPES.map((docType) => renderDocCard(docType))}
            <FormControlLabel
                control={<Checkbox checked={cutoverNotRequired} onChange={(_, checked) => onToggleCutover(checked)} disabled={cutoverDisabled} />}
                label={translate('documents.cutoverNotRequired')}
            />
            {cutoverNotRequired && hasCutoverDoc && (
                <Typography variant="caption" color="warning.main">
                    {translate('documents.cutoverWillBeDisregarded')}
                </Typography>
            )}
            {showCutoverCard && renderDocCard(CUTOVER_DOC_TYPE)}
            {bottomSection}
        </Stack>
    );
}
